import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebaseConfig';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { IoArrowBack, IoPeople, IoChatbubbleEllipses, IoPersonCircleOutline, IoMap } from 'react-icons/io5';
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import { useAuth } from '../context/AuthContext';
import './VigilantesListScreen.css';

function VigilantesListScreen() {
    const navigate = useNavigate();
    const { userData, currentUser } = useAuth(); // Get userData for condo ID
    const [vigilantes, setVigilantes] = useState([]);
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('vigilantes'); // 'vigilantes' | 'residentes'
    const [activeChats, setActiveChats] = useState([]);
    const [unreadCounts, setUnreadCounts] = useState({});

    // 1. Subscribe to Vigilantes List
    useEffect(() => {
        if (!userData || !userData.condominioId) return;

        const qVigilantes = query(
            collection(db, "usuarios"),
            where("rol", "==", "vigilante"),
            where("condominioId", "==", userData.condominioId)
        );

        const unsubVigilantes = onSnapshot(qVigilantes, (snapshot) => {
            const lista = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setVigilantes(lista);
            setLoading(false);
        }, (error) => {
            console.error("Error al cargar vigilantes:", error);
            setLoading(false);
        });

        return () => unsubVigilantes();
    }, [userData]);

    // 1.1 Subscribe to Admin/Comite List (for Residents)
    useEffect(() => {
        if (!userData || !userData.condominioId || userData.rol !== 'residente') return;

        const qAdmins = query(
            collection(db, "usuarios"),
            where("condominioId", "==", userData.condominioId),
            where("rol", "in", ["admin", "comite"])
        );

        const unsubAdmins = onSnapshot(qAdmins, (snapshot) => {
            const lista = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setAdmins(lista);
        }, (error) => {
            console.error("Error al cargar administradores:", error);
        });

        return () => unsubAdmins();
    }, [userData]);

    // 2. Subscribe to My Chats to get Unread Counts and Active Chats List
    useEffect(() => {
        if (!currentUser) return;

        const uid = currentUser.uid;
        const qChats = query(
            collection(db, "chats"),
            where("participants", "array-contains", uid)
        );

        const unsubChats = onSnapshot(qChats, (snapshot) => {
            const counts = {};
            const chatList = [];
            snapshot.docs.forEach(docSnap => {
                const data = docSnap.data();
                const unread = data[`unread_${uid}`] || 0;
                const otherId = data.participants.find(p => p !== uid);
                if (otherId) {
                    if (unread > 0) {
                        counts[otherId] = unread;
                    }
                    
                    const otherName = data.participantNames ? data.participantNames[otherId] : "Residente";
                    
                    chatList.push({
                        id: docSnap.id,
                        otherId,
                        otherName,
                        lastMessage: data.lastMessage || "",
                        unread,
                        lastMessageTime: data.lastMessageTime
                    });
                }
            });
            setUnreadCounts(counts);
            setActiveChats(chatList);
        });

        return () => unsubChats();
    }, [currentUser]);

    const handleChat = (e, vigilanteId) => {
        e.stopPropagation();
        navigate(`/chat/${vigilanteId}`);
    };

    const handleLocationClick = (e, v) => {
        e.stopPropagation();
        if (v.liveLocation && v.liveLocation.inRondin) {
            navigate(`/seguridad/ubicacion/${v.id}`);
        } else {
            alert("El vigilante se encuentra en caseta / No hay rondín activo en este momento.");
        }
    };

    const residentChats = activeChats.filter(chat => !vigilantes.some(v => v.id === chat.otherId));

    return (
        <div className="ks-page">
            <KondoSidebar activeRoute="/seguridad" />
            <main className="ks-main">
                <div className="ks-header-sticky-wrapper">
                    <KondoBrandHeader showBack={false} />
                </div>
                <div className="ks-scroll-area">
                    <div className="vigilantes-list-container" style={{ padding: '15px' }}>
                        {/* Header Banner */}
                        <div className="app-header-sticky" style={{ padding: 0, marginBottom: '15px' }}>
                            <div className="kondo-banner-standard" style={{ background: 'linear-gradient(135deg, #4f46e5 0%, #3730a3 100%)', margin: 0 }}>
                                <div className="banner-standard-content">
                                    <button onClick={() => navigate('/seguridad')} className="banner-back-btn-standard">
                                        <IoArrowBack size={24} />
                                    </button>
                                    <div className="banner-standard-text">
                                        <h2>{['admin', 'comite', 'masteruser', 'superuser', 'residente'].includes(userData?.rol) ? 'Comunicaciones' : 'Vigilantes'}</h2>
                                        <p>{['admin', 'comite', 'masteruser', 'superuser', 'residente'].includes(userData?.rol) ? 'Gestión de contacto' : 'Personal de seguridad'}</p>
                                    </div>
                                </div>
                                <div className="banner-standard-actions">
                                    <IoPeople size={22} color="white" style={{ opacity: 0.8 }} />
                                </div>
                                <IoPeople className="banner-standard-icon-decor" />
                             </div>
                        </div>

                        {/* Tab Selector for Administrators and Residents */}
                        {['admin', 'comite', 'masteruser', 'superuser', 'residente'].includes(userData?.rol) && (
                            <div className="avatar-modal-tabs" style={{ display: 'flex', gap: '10px', marginBottom: '20px', background: '#f1f5f9', padding: '6px', borderRadius: '14px', border: '1px solid #e2e8f0' }}>
                                <button
                                    type="button"
                                    onClick={() => setActiveTab('vigilantes')}
                                    style={{
                                        flex: 1, padding: '12px 16px', border: 'none', borderRadius: '10px', fontSize: '0.9rem', fontWeight: '700', cursor: 'pointer', transition: 'all 0.2s',
                                        background: activeTab === 'vigilantes' ? 'white' : 'none',
                                        color: activeTab === 'vigilantes' ? '#4f46e5' : '#64748b',
                                        boxShadow: activeTab === 'vigilantes' ? '0 4px 6px rgba(0,0,0,0.05)' : 'none'
                                    }}
                                >
                                    Vigilantes en Turno
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setActiveTab('residentes')}
                                    style={{
                                        flex: 1, padding: '12px 16px', border: 'none', borderRadius: '10px', fontSize: '0.9rem', fontWeight: '700', cursor: 'pointer', transition: 'all 0.2s', position: 'relative',
                                        background: activeTab === 'residentes' ? 'white' : 'none',
                                        color: activeTab === 'residentes' ? '#4f46e5' : '#64748b',
                                        boxShadow: activeTab === 'residentes' ? '0 4px 6px rgba(0,0,0,0.05)' : 'none'
                                    }}
                                >
                                    {['admin', 'comite', 'masteruser', 'superuser'].includes(userData?.rol) ? 'Chats con Residentes' : 'Contacto Administración'}
                                    {userData?.rol === 'residente' ? (
                                        activeChats.filter(chat => admins.some(a => a.id === chat.otherId)).reduce((acc, c) => acc + c.unread, 0) > 0 && (
                                            <span style={{
                                                position: 'absolute', top: '6px', right: '12px', background: '#ef4444', color: 'white',
                                                fontSize: '0.7rem', fontWeight: 'bold', minWidth: '18px', height: '18px', borderRadius: '50%',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px'
                                            }}>
                                                {activeChats.filter(chat => admins.some(a => a.id === chat.otherId)).reduce((acc, c) => acc + c.unread, 0)}
                                            </span>
                                        )
                                    ) : (
                                        residentChats.reduce((acc, c) => acc + c.unread, 0) > 0 && (
                                            <span style={{
                                                position: 'absolute', top: '6px', right: '12px', background: '#ef4444', color: 'white',
                                                fontSize: '0.7rem', fontWeight: 'bold', minWidth: '18px', height: '18px', borderRadius: '50%',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px'
                                            }}>
                                                {residentChats.reduce((acc, c) => acc + c.unread, 0)}
                                            </span>
                                        )
                                    )}
                                </button>
                            </div>
                        )}

                        {loading && <p style={{ textAlign: 'center', color: '#6b7280', marginTop: '20px' }}>Cargando...</p>}

                        {/* List 1: Vigilantes */}
                        {!loading && activeTab === 'vigilantes' && vigilantes.length === 0 && (
                            <div className="empty-state" style={{ textAlign: 'center', marginTop: '40px', color: '#6b7280' }}>
                                <IoPeople size={50} style={{ opacity: 0.5, marginBottom: '10px' }} />
                                <p>No se encontraron vigilantes registrados.</p>
                            </div>
                        )}

                        {!loading && activeTab === 'vigilantes' && vigilantes.length > 0 && (
                            <div className="vigilantes-list" style={{ padding: 0 }}>
                                {vigilantes.map((v) => (
                                    <div key={v.id} className="vigilante-card">
                                        <div className="vigilante-avatar-container">
                                            {v.fotoPerfilUrl ? (
                                                <img src={v.fotoPerfilUrl} alt={v.nombre} className="vigilante-avatar" />
                                            ) : (
                                                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#e5e7eb' }}>
                                                    <IoPersonCircleOutline size={30} color="#9ca3af" />
                                                </div>
                                            )}
                                        </div>
                                        <div className="vigilante-info">
                                            <h3>{v.nombre}</h3>
                                            <div className="vigilante-status">
                                                <span className="status-dot"></span>
                                                En Turno
                                            </div>
                                        </div>
                                        <div className="action-container" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <button className="btn-location" onClick={(e) => handleLocationClick(e, v)}>
                                                <IoMap />
                                            </button>
                                            {unreadCounts[v.id] > 0 && (
                                                <div className="notification-badge" style={{
                                                    background: '#ef4444', color: 'white', fontSize: '0.75rem', fontWeight: 'bold',
                                                    width: '22px', height: '22px', borderRadius: '50%', display: 'flex',
                                                    alignItems: 'center', justifyContent: 'center'
                                                }}>
                                                    {unreadCounts[v.id]}
                                                </div>
                                            )}
                                            <button className="btn-chat" onClick={(e) => handleChat(e, v.id)}>
                                                <IoChatbubbleEllipses />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* List 2: Active Resident Chats for Admins / Admin Contacts for Residents */}
                        {!loading && activeTab === 'residentes' && (
                            <div className="vigilantes-list" style={{ padding: 0 }}>
                                {userData?.rol === 'residente' ? (
                                    admins.length === 0 ? (
                                        <div className="empty-state" style={{ textAlign: 'center', marginTop: '40px', color: '#6b7280' }}>
                                            <IoPersonCircleOutline size={50} style={{ opacity: 0.5, marginBottom: '10px' }} />
                                            <p>No se encontraron administradores registrados.</p>
                                        </div>
                                    ) : (
                                        admins.map((admin) => (
                                            <div key={admin.id} className="vigilante-card">
                                                <div className="vigilante-avatar-container">
                                                    {admin.fotoPerfilUrl ? (
                                                        <img src={admin.fotoPerfilUrl} alt={admin.nombre} className="vigilante-avatar" />
                                                    ) : (
                                                        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#e0e7ff' }}>
                                                            <IoPersonCircleOutline size={30} color="#4f46e5" />
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="vigilante-info" style={{ minWidth: 0 }}>
                                                    <h3 style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>{admin.nombre}</h3>
                                                    <div className="vigilante-status" style={{ color: '#4f46e5', fontWeight: '600' }}>
                                                        {admin.rol === 'admin' ? 'Administrador' : 'Comité de Vigilancia'}
                                                    </div>
                                                </div>
                                                <div className="action-container" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginLeft: 'auto', flexShrink: 0 }}>
                                                    {unreadCounts[admin.id] > 0 && (
                                                        <div className="notification-badge" style={{
                                                            background: '#ef4444', color: 'white', fontSize: '0.75rem', fontWeight: 'bold',
                                                            width: '22px', height: '22px', borderRadius: '50%', display: 'flex',
                                                            alignItems: 'center', justifyContent: 'center'
                                                        }}>
                                                            {unreadCounts[admin.id]}
                                                        </div>
                                                    )}
                                                    <button className="btn-chat" onClick={(e) => handleChat(e, admin.id)}>
                                                        <IoChatbubbleEllipses />
                                                    </button>
                                                </div>
                                            </div>
                                        ))
                                    )
                                ) : (
                                    residentChats.length === 0 ? (
                                        <div className="empty-state" style={{ textAlign: 'center', marginTop: '40px', color: '#6b7280' }}>
                                            <IoChatbubbleEllipses size={50} style={{ opacity: 0.5, marginBottom: '10px' }} />
                                            <p>No tienes chats activos con residentes.</p>
                                        </div>
                                    ) : (
                                        residentChats.map((chat) => (
                                            <div key={chat.id} className="vigilante-card" onClick={(e) => handleChat(e, chat.otherId)} style={{ cursor: 'pointer' }}>
                                                <div className="vigilante-avatar-container">
                                                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#e0e7ff' }}>
                                                        <IoPersonCircleOutline size={30} color="#4f46e5" />
                                                    </div>
                                                </div>
                                                <div className="vigilante-info" style={{ minWidth: 0 }}>
                                                    <h3 style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>{chat.otherName}</h3>
                                                    <div className="vigilante-status" style={{ color: '#64748b', fontSize: '0.82rem', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', display: 'block', marginTop: '4px' }}>
                                                        {chat.lastMessage || "Sin mensajes aún"}
                                                    </div>
                                                </div>
                                                <div className="action-container" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginLeft: 'auto', flexShrink: 0 }}>
                                                    {chat.unread > 0 && (
                                                        <div className="notification-badge" style={{
                                                            background: '#ef4444', color: 'white', fontSize: '0.75rem', fontWeight: 'bold',
                                                            width: '22px', height: '22px', borderRadius: '50%', display: 'flex',
                                                            alignItems: 'center', justifyContent: 'center'
                                                        }}>
                                                            {chat.unread}
                                                        </div>
                                                    )}
                                                    <button className="btn-chat" onClick={(e) => handleChat(e, chat.otherId)}>
                                                        <IoChatbubbleEllipses />
                                                    </button>
                                                </div>
                                            </div>
                                        ))
                                    )
                                )}
                            </div>
                        )}
                    </div>
                </div>
                <KondoBottomNav activeRoute="/seguridad" />
            </main>
        </div>
    );
}

export default VigilantesListScreen;
